<script setup>
import { computed, ref, watch, nextTick, onMounted } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean],
    required: true,
  },
  options: {
    type: Array,
    required: true,
    validator: options => {
      return options.every(
        option =>
          typeof option === 'object' && 'value' in option && 'label' in option
      );
    },
  },
  size: {
    type: String,
    default: 'medium',
    validator: value => ['small', 'medium', 'large'].includes(value),
  },
});

const emit = defineEmits(['update:modelValue']);

// Store option element refs
const optionElements = ref({});
const optionWidths = ref([]);

// Update widths when options or refs change
const updateWidths = async () => {
  await nextTick();
  const widths = [];
  for (let i = 0; i < props.options.length; i += 1) {
    const el = optionElements.value[i];
    widths.push(el?.offsetWidth || 0);
  }
  optionWidths.value = widths;
};

// Watch for changes that require width recalculation
watch(() => props.options, updateWidths, { deep: true });
watch(
  () => props.modelValue,
  () => {
    nextTick(updateWidths);
  }
);

onMounted(() => {
  updateWidths();
});

const activeIndex = computed(() => {
  return props.options.findIndex(opt => opt.value === props.modelValue);
});

const sizeClasses = computed(() => {
  const sizes = {
    small: 'h-7 text-xs',
    medium: 'h-8 text-sm',
    large: 'h-9 text-base',
  };
  return sizes[props.size];
});

// Calculate the width of the sliding background
const chipWidth = computed(() => {
  if (activeIndex.value >= 0 && optionWidths.value[activeIndex.value]) {
    return `${optionWidths.value[activeIndex.value]}px`;
  }
  return '0px';
});

// Calculate the translate value for the sliding background
const translateValue = computed(() => {
  if (activeIndex.value <= 0) return '0px';

  let totalWidth = 0;
  for (let i = 0; i < activeIndex.value; i += 1) {
    if (optionWidths.value[i]) {
      totalWidth += optionWidths.value[i];
    }
  }
  return `${totalWidth}px`;
});

const handleOptionClick = value => {
  emit('update:modelValue', value);
};
</script>

<template>
  <button
    type="button"
    class="flex items-center w-auto p-1 transition-all border rounded-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 group relative duration-300 ease-in-out z-0 font-medium"
    :class="sizeClasses"
  >
    <div
      v-for="(option, index) in options"
      :key="option.value"
      :ref="
        el => {
          if (el) optionElements[index] = el;
        }
      "
      class="flex items-center gap-1.5 px-2 z-20 cursor-pointer transition-colors duration-200"
      :class="{
        'text-white': modelValue === option.value,
        'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200':
          modelValue !== option.value,
      }"
      @click="handleOptionClick(option.value)"
    >
      <Icon v-if="option.icon" :icon="option.icon" class="w-4 h-4 shrink-0" />
      <span class="whitespace-nowrap">{{ option.label }}</span>
    </div>

    <!-- Sliding background chip -->
    <div
      v-if="activeIndex >= 0"
      class="absolute shadow-sm rounded-full h-6 w-[var(--chip-width)] transition-all duration-300 ease-in-out translate-x-[var(--translate-x)] rtl:translate-x-[var(--rtl-translate-x)] bg-n-brand"
      :style="{
        '--chip-width': chipWidth,
        '--translate-x': translateValue,
        '--rtl-translate-x': `calc(-1 * var(--translate-x))`,
      }"
    />
  </button>
</template>
